import React, { useState, useRef, useEffect, useMemo } from 'react';
import { 
  Map, 
  ChevronDown, 
  Plus, 
  Copy, 
  Edit2, 
  Trash2, 
  Check, 
  X
} from 'lucide-react';
import './MapSwitcher.css';

const MAP_STYLES = [
  { id: 'default', title: 'Default Dark Fog', desc: 'Dark base with glowing H3 tiles' },
  { id: 'circled', title: 'Hand-Drawn Circles', desc: 'Ink pen outline circles' },
  { id: 'pins', title: 'Map Pins', desc: 'Clean base with glowing pins' },
  { id: 'pins-label', title: 'Pins with Label', desc: 'Pins showing custom labels' }
];

function getStyleTitle(styleId) {
  const found = MAP_STYLES.find(s => s.id === styleId);
  return found ? found.title : 'Dark Fog';
}

function MapSwitcher({
  maps = [],
  activeMapId,
  onSelectMap,
  onCreateMap,
  onDuplicateMap,
  onUpdateMap,
  onDeleteMap,
  isLoading = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modals state: null | 'new' | 'edit' | 'duplicate' | 'delete'
  const [modalType, setModalType] = useState(null);
  const [targetMap, setTargetMap] = useState(null);

  // Modal form states
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formStyle, setFormStyle] = useState('default');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const containerRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (modalType) {
          closeModal();
        } else if (isOpen) {
          setIsOpen(false);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, modalType]);

  const activeMap = useMemo(() => {
    return maps.find(m => m.id === activeMapId) || maps[0] || null;
  }, [maps, activeMapId]);

  const filteredMaps = useMemo(() => {
    if (!searchQuery.trim()) return maps;
    const q = searchQuery.toLowerCase();
    return maps.filter(m => (m.name || '').toLowerCase().includes(q) || (m.description || '').toLowerCase().includes(q));
  }, [maps, searchQuery]);

  const openNewModal = () => {
    setIsOpen(false);
    setFormName(`Map ${maps.length + 1}`);
    setFormDesc('');
    setFormStyle('default');
    setFormError('');
    setTargetMap(null);
    setModalType('new');
  };

  const openEditModal = (map, e) => {
    if (e) e.stopPropagation();
    setIsOpen(false);
    setTargetMap(map);
    setFormName(map.name);
    setFormDesc(map.description || '');
    setFormStyle(map.map_style || 'default');
    setFormError('');
    setModalType('edit');
  };

  const openDuplicateModal = (map, e) => {
    if (e) e.stopPropagation();
    setIsOpen(false);
    setTargetMap(map);
    setFormName(`Copy of ${map.name}`);
    setFormError('');
    setModalType('duplicate');
  };

  const openDeleteModal = (map, e) => {
    if (e) e.stopPropagation();
    if (maps.length <= 1) return; // Prevent deleting only map
    setIsOpen(false);
    setTargetMap(map);
    setFormError('');
    setModalType('delete');
  };

  const closeModal = () => {
    setModalType(null);
    setTargetMap(null);
    setFormError('');
    setIsSubmitting(false);
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!formName.trim()) {
      setFormError('Map name is required.');
      return;
    }
    setIsSubmitting(true);
    setFormError('');
    try {
      await onCreateMap({
        name: formName.trim(),
        description: formDesc.trim() || undefined,
        map_style: formStyle
      });
      closeModal();
    } catch (err) {
      setFormError(err.message || 'Failed to create map.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!formName.trim()) {
      setFormError('Map name is required.');
      return;
    }
    setIsSubmitting(true);
    setFormError('');
    try {
      await onUpdateMap(targetMap.id, {
        name: formName.trim(),
        description: formDesc.trim() || undefined,
        map_style: formStyle
      });
      closeModal();
    } catch (err) {
      setFormError(err.message || 'Failed to update map.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDuplicateSubmit = async (e) => {
    e.preventDefault();
    if (!formName.trim()) {
      setFormError('Map name is required.');
      return;
    }
    setIsSubmitting(true);
    setFormError('');
    try {
      await onDuplicateMap(targetMap.id, formName.trim());
      closeModal();
    } catch (err) {
      setFormError(err.message || 'Failed to duplicate map.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSubmit = async () => {
    if (!targetMap) return;
    setIsSubmitting(true);
    setFormError('');
    try {
      await onDeleteMap(targetMap.id);
      closeModal();
    } catch (err) {
      setFormError(err.message || 'Failed to delete map.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="map-switcher-container" ref={containerRef}>
      {/* Active Map Card Trigger */}
      <button
        type="button"
        className={`map-switcher-card ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Switch or manage map projects"
      >
        <div className="map-card-left">
          <div className="map-card-icon-box">
            <Map size={18} />
          </div>
          <div className="map-card-text">
            <span className="map-card-tag">Active Map</span>
            <span className="map-card-name">{activeMap?.name || 'My World Map'}</span>
            <span className="map-card-subtext">
              {activeMap?.places_count !== undefined ? `${activeMap.places_count} places` : ''} 
              {activeMap?.map_style ? ` • ${getStyleTitle(activeMap.map_style)}` : ''}
            </span>
          </div>
        </div>
        <ChevronDown size={16} className={`map-card-chevron ${isOpen ? 'rotated' : ''}`} />
      </button>

      {/* Dropdown Menu Popover */}
      {isOpen && (
        <div className="map-dropdown-menu">
          <div className="map-dropdown-header">
            <span className="map-dropdown-title">Map Projects</span>
            <span className="map-dropdown-count">{maps.length} {maps.length === 1 ? 'map' : 'maps'}</span>
          </div>

          {maps.length > 4 && (
            <div className="map-dropdown-search-wrap">
              <input
                type="text"
                className="map-dropdown-search"
                placeholder="Search maps..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>
          )}

          <div className="map-dropdown-list">
            {filteredMaps.map((m) => {
              const isItemActive = m.id === activeMapId;
              return (
                <div
                  key={m.id}
                  className={`map-dropdown-item ${isItemActive ? 'active' : ''}`}
                  onClick={() => {
                    if (!isItemActive) {
                      onSelectMap(m.id);
                    }
                    setIsOpen(false);
                  }}
                >
                  <div className="map-item-main">
                    <div className="map-item-indicator">
                      {isItemActive ? <Check size={16} /> : <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'rgba(255,255,255,0.2)' }} />}
                    </div>
                    <div className="map-item-info">
                      <span className="map-item-name">{m.name}</span>
                      <div className="map-item-meta">
                        <span>{m.places_count || 0} places</span>
                        <span>•</span>
                        <span className="map-style-badge">{getStyleTitle(m.map_style)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="map-item-actions">
                    <button
                      type="button"
                      className="map-action-icon-btn"
                      title="Duplicate this map"
                      onClick={(e) => openDuplicateModal(m, e)}
                    >
                      <Copy size={13} />
                    </button>
                    <button
                      type="button"
                      className="map-action-icon-btn"
                      title="Rename or edit details"
                      onClick={(e) => openEditModal(m, e)}
                    >
                      <Edit2 size={13} />
                    </button>
                    {maps.length > 1 && (
                      <button
                        type="button"
                        className="map-action-icon-btn delete"
                        title="Delete map"
                        onClick={(e) => openDeleteModal(m, e)}
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="map-dropdown-footer">
            <button
              type="button"
              className="new-map-trigger-btn"
              onClick={openNewModal}
            >
              <Plus size={14} />
              <span>Create New Map</span>
            </button>
          </div>
        </div>
      )}

      {/* --- Modals --- */}

      {/* 1. New Map Modal */}
      {modalType === 'new' && (
        <div className="map-modal-backdrop" onClick={closeModal}>
          <div className="map-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="map-modal-header">
              <h3 className="map-modal-title">Create New Map Project</h3>
              <button type="button" className="map-action-icon-btn" onClick={closeModal}>
                <X size={16} />
              </button>
            </div>
            <form onSubmit={handleCreateSubmit}>
              <div className="map-modal-body">
                {formError && <div className="contact-error-banner" style={{ margin: 0 }}>{formError}</div>}
                
                <div className="map-modal-field">
                  <label className="map-modal-label">Map Project Name</label>
                  <input
                    type="text"
                    className="map-modal-input"
                    placeholder="e.g. Silk Road Journey, Greek Odyssey..."
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    required
                    autoFocus
                  />
                </div>

                <div className="map-modal-field">
                  <label className="map-modal-label">Description (Optional)</label>
                  <textarea
                    className="map-modal-textarea"
                    placeholder="Brief notes about this journey..."
                    rows={2}
                    value={formDesc}
                    onChange={(e) => setFormDesc(e.target.value)}
                  />
                </div>

                <div className="map-modal-field">
                  <label className="map-modal-label">Map Style</label>
                  <div className="map-style-selector-grid">
                    {MAP_STYLES.map((st) => (
                      <button
                        key={st.id}
                        type="button"
                        className={`map-style-option-btn ${formStyle === st.id ? 'selected' : ''}`}
                        onClick={() => setFormStyle(st.id)}
                      >
                        <span className="style-opt-title">{st.title}</span>
                        <span className="style-opt-desc">{st.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="map-modal-actions">
                <button type="button" className="map-btn-secondary" onClick={closeModal} disabled={isSubmitting}>
                  Cancel
                </button>
                <button type="submit" className="map-btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Creating...' : 'Create Map'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 2. Edit Map Modal */}
      {modalType === 'edit' && targetMap && (
        <div className="map-modal-backdrop" onClick={closeModal}>
          <div className="map-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="map-modal-header">
              <h3 className="map-modal-title">Edit Map Project</h3>
              <button type="button" className="map-action-icon-btn" onClick={closeModal}>
                <X size={16} />
              </button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="map-modal-body">
                {formError && <div className="contact-error-banner" style={{ margin: 0 }}>{formError}</div>}
                
                <div className="map-modal-field">
                  <label className="map-modal-label">Map Project Name</label>
                  <input
                    type="text"
                    className="map-modal-input"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    required
                    autoFocus
                  />
                </div>

                <div className="map-modal-field">
                  <label className="map-modal-label">Description (Optional)</label>
                  <textarea
                    className="map-modal-textarea"
                    rows={2}
                    value={formDesc}
                    onChange={(e) => setFormDesc(e.target.value)}
                  />
                </div>

                <div className="map-modal-field">
                  <label className="map-modal-label">Map Style</label>
                  <div className="map-style-selector-grid">
                    {MAP_STYLES.map((st) => (
                      <button
                        key={st.id}
                        type="button"
                        className={`map-style-option-btn ${formStyle === st.id ? 'selected' : ''}`}
                        onClick={() => setFormStyle(st.id)}
                      >
                        <span className="style-opt-title">{st.title}</span>
                        <span className="style-opt-desc">{st.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="map-modal-actions">
                <button type="button" className="map-btn-secondary" onClick={closeModal} disabled={isSubmitting}>
                  Cancel
                </button>
                <button type="submit" className="map-btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 3. Duplicate Map Modal */}
      {modalType === 'duplicate' && targetMap && (
        <div className="map-modal-backdrop" onClick={closeModal}>
          <div className="map-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="map-modal-header">
              <h3 className="map-modal-title">Duplicate Map Project</h3>
              <button type="button" className="map-action-icon-btn" onClick={closeModal}>
                <X size={16} />
              </button>
            </div>
            <form onSubmit={handleDuplicateSubmit}>
              <div className="map-modal-body">
                {formError && <div className="contact-error-banner" style={{ margin: 0 }}>{formError}</div>}
                
                <p style={{ margin: 0, fontSize: '13px', color: '#8b949e', lineHeight: 1.5 }}>
                  This creates an independent copy of <strong>{targetMap.name}</strong>, including all its locations, uncovered fog, and styling.
                </p>

                <div className="map-modal-field">
                  <label className="map-modal-label">New Map Name</label>
                  <input
                    type="text"
                    className="map-modal-input"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
              </div>
              <div className="map-modal-actions">
                <button type="button" className="map-btn-secondary" onClick={closeModal} disabled={isSubmitting}>
                  Cancel
                </button>
                <button type="submit" className="map-btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Duplicating...' : 'Duplicate Map'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 4. Delete Map Confirmation Modal */}
      {modalType === 'delete' && targetMap && (
        <div className="map-modal-backdrop" onClick={closeModal}>
          <div className="map-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="map-modal-header">
              <h3 className="map-modal-title">Delete Map Project?</h3>
              <button type="button" className="map-action-icon-btn" onClick={closeModal}>
                <X size={16} />
              </button>
            </div>
            <div className="map-modal-body">
              {formError && <div className="contact-error-banner" style={{ margin: 0 }}>{formError}</div>}
              <p style={{ margin: 0, fontSize: '13px', color: '#c9d1d9', lineHeight: 1.5 }}>
                Are you sure you want to permanently delete <strong>{targetMap.name}</strong>?
              </p>
              <p style={{ margin: 0, fontSize: '12px', color: '#8b949e', lineHeight: 1.4 }}>
                All {targetMap.places_count || 0} locations and uncovered fog associated with this map will be removed. This action cannot be undone.
              </p>
            </div>
            <div className="map-modal-actions">
              <button type="button" className="map-btn-secondary" onClick={closeModal} disabled={isSubmitting}>
                Cancel
              </button>
              <button type="button" className="map-btn-danger" onClick={handleDeleteSubmit} disabled={isSubmitting}>
                {isSubmitting ? 'Deleting...' : 'Delete Map'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MapSwitcher;
